import React, { useLayoutEffect, useRef, useState } from 'react'

import * as S from './collapse.styled'
import { CollapseProps } from './collapse.types'

export const Collapse = ({
  in: isExpanded,
  animation = 'collapse',
  collapsedSize = 0,
  timeout = 'medium',
  watch = [],
  onOverflowChange,
  children,
  ...rest
}: CollapseProps) => {
  const [scrollHeight, setScrollHeight] = useState(0)
  const contentRef = useRef<HTMLDivElement>(null)

  const collapsedSizePx = collapsedSize * 10

  // Note: this component's root sets `overflow: hidden` (needed for the max-height
  // animation), which resolves its flex automatic min-height to 0 - if a consumer renders
  // it as a child of a `flex-direction: column` container with a fixed `height` (e.g. this
  // project's `Page`/`PageWrapper`), it becomes the item the flex algorithm shrinks first
  // whenever total content exceeds that fixed height, clipping it below the size computed
  // here. If content looks cut off despite this component reporting the right height,
  // check the parent's flex layout first and add `flex-shrink: 0` to this component's
  // wrapper (see reservations.page.styled.ts's `AnalyticsCollapse` for the pattern).
  useLayoutEffect(() => {
    if (animation !== 'collapse' && animation !== 'grow') return
    const el = contentRef.current
    if (!el) return

    const measure = () => {
      setScrollHeight(el.scrollHeight)
      onOverflowChange?.(el.scrollHeight > collapsedSizePx)
    }

    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(el)

    return () => observer.disconnect()
  }, [...watch, collapsedSizePx, animation])

  return (
    <S.Root
      $isExpanded={isExpanded}
      $animation={animation}
      $scrollHeight={scrollHeight}
      $collapsedSizeRem={collapsedSize}
      $timeout={timeout}
      {...rest}
    >
      <div ref={contentRef}>{children}</div>
    </S.Root>
  )
}
